<div class="workspace-selection-container" [class.workspace-selection-container-busy]="isUiBlocked">
  @if (isUiBlocked) {
    <div class="interaction-shield" aria-live="polite" aria-busy="true">
      <div class="interaction-shield__panel">
        <div class="spinner spinner-overlay"></div>
        <strong>{{ busyLabel || ('ws.busy.default' | translate) }}</strong>
        <span>{{ 'ws.busy.hint' | translate }}</span>
      </div>
    </div>
  }

  <div class="picker-shell">
    <header class="picker-header">
      <div class="picker-brand">
        <p class="picker-eyebrow">{{ 'ws.eyebrow' | translate }}</p>
        <h1>{{ 'ws.title' | translate }}</h1>
        <p>{{ 'ws.subtitle' | translate }}</p>
      </div>

      <div class="picker-header__actions">
        <app-language-switcher></app-language-switcher>

        <button type="button" class="logout-button" [disabled]="isUiBlocked" (click)="logout()">
          {{ 'ws.logout' | translate }}
        </button>
      </div>
    </header>

    @if (emptyStateTitle && !authCompanySelectionMode && !selectedCompany && companies.length === 0) {
      <section class="status-panel status-panel-warning">
        <h2>{{ emptyStateTitle }}</h2>
        <p>{{ emptyStateMessage }}</p>

        @if (restrictedCompanies.length > 0) {
          <div class="restricted-list">
            @for (item of restrictedCompanies; track $index) {
              <div class="restricted-item">
                <strong>{{ item.companyName || ('ws.restricted.companyFallback' | translate) }}</strong>
                <span>{{ item.message || item.reason || ('ws.restricted.reasonFallback' | translate) }}</span>
              </div>
            }
          </div>
        }
      </section>
    } @else if (authCompanySelectionMode) {
      <section class="status-panel auth-company-panel">
        <div class="status-panel__copy">
          <span class="status-badge">{{ 'ws.company.badge' | translate }}</span>
          <h2>{{ 'ws.authCompany.title' | translate }}</h2>
          <p>{{ 'ws.authCompany.description' | translate }}</p>
        </div>

        <div class="company-grid">
          @for (company of pendingAuthCompanies; track company.companyPlatformId) {
            <button type="button" class="company-card" [disabled]="isUiBlocked" (click)="selectAuthCompany(company)">
              <div class="company-card__topline">
                <span class="company-card__badge">{{ 'ws.company.badge' | translate }}</span>
                <span class="company-card__action">{{ 'ws.company.activate' | translate }}</span>
              </div>
              <div class="company-card__icon">{{ company.companyName.charAt(0) }}</div>
              <div class="company-card__body">
                <h3>{{ company.companyName }}</h3>
                <p>{{ company.role || platformName }}</p>
              </div>
            </button>
          }
        </div>
      </section>
    } @else {
      <section class="company-strip">
        <div class="company-strip__main">
          <span class="company-strip__label">{{ 'ws.strip.envLabel' | translate }}</span>
          <strong class="company-strip__value">{{ platformName }}</strong>
          <span class="company-strip__hint">
            @if (selectedCompany) {
              {{ selectedCompany.companyPlatform.company.businessName }}
            } @else {
              {{ 'ws.strip.selectHint' | translate }}
            }
          </span>
        </div>

        @if (!selectedCompany) {
          <div class="company-strip__selector">
            @for (company of companies; track company.id) {
              <button
                type="button"
                class="company-pill"
                [disabled]="isUiBlocked"
                (click)="selectCompany(company)">
                <span class="company-pill__name">{{ company.companyPlatform.company.businessName }}</span>
                <span class="company-pill__meta">{{ company.companyPlatform.platform?.name || ('ws.platformFallback' | translate) }}</span>
              </button>
            }
          </div>
        } @else {
          <div class="company-strip__summary">
            <button
              type="button"
              class="context-chip"
              [disabled]="isUiBlocked"
              [class.context-chip-static]="companies.length <= 1"
              (click)="companies.length > 1 && resetCompanySelection()">
              <span class="context-label">{{ 'ws.context.company' | translate }}</span>
              <strong>{{ selectedCompany.companyPlatform.company.businessName }}</strong>
            </button>
          </div>
        }
      </section>

      <main class="picker-grid">
        <section
          class="picker-column"
          [class.picker-column-active]="selectedOrganization"
          [class.picker-column-disabled]="!selectedCompany">
          <header class="picker-column__header">
            <div class="picker-column__title">
              <span class="picker-column__step">1</span>
              <h2>{{ 'ws.org.title' | translate }}</h2>
            </div>
            @if (selectedOrganization) {
              <p class="picker-column__selected">{{ selectedOrganization.name }}</p>
            }
          </header>

          <input
            type="text"
            class="picker-column__search"
            [placeholder]="'ws.org.searchPlaceholder' | translate"
            [disabled]="!selectedCompany || isUiBlocked"
            [(ngModel)]="searchOrganization" />

          <div class="picker-column__body">
            @if (!selectedCompany) {
              <div class="picker-column__state">
                <p>{{ 'ws.org.needCompany' | translate }}</p>
              </div>
            } @else if (!filteredOrganizations.length) {
              <div class="picker-column__state">
                <p>{{ 'ws.org.empty' | translate }}</p>
              </div>
            } @else {
              <ul class="picker-list">
                @for (organization of filteredOrganizations; track organization.id) {
                  <li>
                    <button
                      type="button"
                      class="picker-item"
                      [class.picker-item-active]="selectedOrganization?.id === organization.id"
                      [disabled]="isUiBlocked"
                      (click)="selectOrganization(organization)">
                      <span class="picker-item__icon">{{ organization.name.charAt(0) }}</span>
                      <span class="picker-item__body">
                        <strong>{{ organization.name }}</strong>
                        <span>{{ selectedCompany.companyPlatform.company.businessName }}</span>
                      </span>
                    </button>
                  </li>
                }
              </ul>
            }
          </div>
        </section>

        <section
          class="picker-column"
          [class.picker-column-active]="selectedArea"
          [class.picker-column-disabled]="!selectedOrganization">
          <header class="picker-column__header">
            <div class="picker-column__title">
              <span class="picker-column__step">2</span>
              <h2>{{ 'ws.area.title' | translate }}</h2>
            </div>
            @if (selectedArea) {
              <p class="picker-column__selected">{{ selectedArea.name }}</p>
            }
          </header>

          <input
            type="text"
            class="picker-column__search"
            [placeholder]="'ws.area.searchPlaceholder' | translate"
            [disabled]="!selectedOrganization || isUiBlocked"
            [(ngModel)]="searchArea" />

          <div class="picker-column__body">
            @if (!selectedOrganization) {
              <div class="picker-column__state">
                <p>{{ 'ws.area.needOrg' | translate }}</p>
              </div>
            } @else if (!filteredAreas.length) {
              <div class="picker-column__state">
                <p>{{ 'ws.area.empty' | translate }}</p>
              </div>
            } @else {
              <ul class="picker-list">
                @for (area of filteredAreas; track area.id) {
                  <li>
                    <button
                      type="button"
                      class="picker-item"
                      [class.picker-item-active]="selectedArea?.id === area.id"
                      [disabled]="isUiBlocked"
                      (click)="selectArea(area)">
                      <span class="picker-item__icon">{{ area.name.charAt(0) }}</span>
                      <span class="picker-item__body">
                        <strong>{{ area.name }}</strong>
                        <span>{{ selectedOrganization.name || ('ws.org.fallback' | translate) }}</span>
                      </span>
                    </button>
                  </li>
                }
              </ul>
            }
          </div>
        </section>

        <section
          class="picker-column"
          [class.picker-column-active]="selectedWorkspace"
          [class.picker-column-disabled]="!selectedArea">
          <header class="picker-column__header">
            <div class="picker-column__title">
              <span class="picker-column__step">3</span>
              <h2>{{ 'ws.workspace.title' | translate }}</h2>
            </div>
            @if (selectedWorkspace) {
              <p class="picker-column__selected">{{ selectedWorkspace.name }}</p>
            }
          </header>

          <input
            type="text"
            class="picker-column__search"
            [placeholder]="'ws.workspace.searchPlaceholder' | translate"
            [disabled]="!selectedArea || isUiBlocked"
            [(ngModel)]="searchWorkspace" />

          <div class="picker-column__body">
            @if (!selectedArea) {
              <div class="picker-column__state">
                <p>{{ 'ws.workspace.needArea' | translate }}</p>
              </div>
            } @else if (!filteredWorkspaces.length) {
              <div class="picker-column__state">
                <p>{{ 'ws.workspace.empty' | translate }}</p>
              </div>
            } @else {
              <ul class="picker-list">
                @for (workspace of filteredWorkspaces; track workspace.id) {
                  <li>
                    <button
                      type="button"
                      class="picker-item"
                      [class.picker-item-active]="selectedWorkspace?.id === workspace.id"
                      [disabled]="isUiBlocked"
                      (click)="selectWorkspace(workspace)">
                      <span class="picker-item__icon">{{ workspace.name.charAt(0) }}</span>
                      <span class="picker-item__body">
                        <strong>{{ workspace.name }}</strong>
                        <span>{{ selectedArea.name || ('ws.area.fallback' | translate) }}</span>
                      </span>
                    </button>
                  </li>
                }
              </ul>
            }
          </div>
        </section>
      </main>

      @if (emptyStateTitle && (selectedCompany || selectedOrganization || selectedArea)) {
        <section class="status-panel status-panel-inline">
          <h2>{{ emptyStateTitle }}</h2>
          <p>{{ emptyStateMessage }}</p>
        </section>
      }

      <footer class="picker-footer">
        <div class="picker-breadcrumb">
          @if (selectedCompany) {
            <span>{{ selectedCompany.companyPlatform.company.businessName }}</span>
          }
          @if (selectedOrganization) {
            <i>&rsaquo;</i>
            <span>{{ selectedOrganization.name }}</span>
          }
          @if (selectedArea) {
            <i>&rsaquo;</i>
            <span>{{ selectedArea.name }}</span>
          }
          @if (selectedWorkspace) {
            <i>&rsaquo;</i>
            <span class="picker-breadcrumb__active">{{ selectedWorkspace.name }}</span>
          }
        </div>

        <button
          type="button"
          class="picker-cta"
          [disabled]="!canContinueToWorkspace"
          (click)="continueToWorkspace()">
          {{ 'ws.cta' | translate }}
        </button>
      </footer>
    }
  </div>
</div>
